export type CountryOption = { code: string; label: string }

const DEFAULT_LOCALE = 'en-AU'

/** Minimal fallback when `Intl.supportedValuesOf('region')` is unavailable. */
const FALLBACK_COUNTRY_CODES = [
  'AU',
  'NZ',
  'US',
  'GB',
  'CA',
  'IE',
  'SG',
  'IN',
  'DE',
  'FR',
  'NL',
  'JP',
  'HK',
  'PH',
  'ID',
  'MY',
  'TH',
  'VN',
  'CN',
  'KR',
  'AE',
  'ZA',
  'BR',
  'MX',
  'ES',
  'IT',
  'SE',
  'NO',
  'DK',
  'FI',
  'CH',
  'AT',
  'BE',
  'PL',
  'PT',
  'IL',
  'SA',
  'QA',
]

function regionCodes(): string[] {
  try {
    const supportedValuesOf = (
      Intl as typeof Intl & { supportedValuesOf?: (key: 'region') => string[] }
    ).supportedValuesOf
    if (typeof supportedValuesOf === 'function') {
      return supportedValuesOf('region').filter((code: string) => /^[A-Z]{2}$/.test(code))
    }
  } catch {
    // Some runtimes reject `region` in supportedValuesOf.
  }
  return FALLBACK_COUNTRY_CODES
}

let cachedOptions: Map<string, CountryOption[]> = new Map()

export function getCountryOptions(locale: string = DEFAULT_LOCALE): CountryOption[] {
  const cached = cachedOptions.get(locale)
  if (cached) return cached
  const display = new Intl.DisplayNames([locale], { type: 'region' })
  const options = regionCodes()
    .map((code) => ({ code, label: display.of(code) ?? code }))
    .sort((a, b) => a.label.localeCompare(b.label, locale))
  cachedOptions.set(locale, options)
  return options
}

/** Default ISO country from BCP 47 locale (e.g. en-AU → AU). */
export function defaultTenantCountryCode(locale: string = DEFAULT_LOCALE): string {
  const region = locale.split('-')[1]?.trim().toUpperCase()
  if (region && region.length === 2) return region
  return 'AU'
}

export function normalizeCountryCodeInput(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 2)
}

export function getCountryLabel(code: string, locale: string = DEFAULT_LOCALE): string {
  const normalized = code.trim().toUpperCase()
  if (!normalized) return ''
  const hit = getCountryOptions(locale).find((option) => option.code === normalized)
  return hit?.label ?? normalized
}

export function filterCountryOptions(query: string, locale: string = DEFAULT_LOCALE): CountryOption[] {
  const trimmed = query.trim().toLowerCase()
  const all = getCountryOptions(locale)
  if (!trimmed) return all
  return all.filter(
    (option) =>
      option.label.toLowerCase().includes(trimmed) || option.code.toLowerCase().includes(trimmed),
  )
}

export function isValidCountryCode(code: string, locale: string = DEFAULT_LOCALE): boolean {
  const normalized = code.trim().toUpperCase()
  if (normalized.length !== 2) return false
  return getCountryOptions(locale).some((option) => option.code === normalized)
}
